import { escapeRegexp } from '../../../../../../lib/escapeRegex';
import { currentMatchMarkStyle, regularMatchMarkStyle, styleObjectToString } from './markStyles';
import { PossibleSectionInDependencies } from '../types';
import { shouldIncludeTextNode, cleanupHighlights } from '../sharedDomHelpers';
import { colors } from '@sweep-io/sweep-design';
import { SearchResult } from '../SearchContext';

// Constants for DOM traversal and highlighting
export const MAX_PARENT_DEPTH = 10; // Maximum depth to search for parent elements
export const ROW_HIGHLIGHT_ATTR = 'data-search-row-highlight';
export const ROW_HIGHLIGHT_CLASS = 'search-highlight-row';
export const CURRENT_MATCH_CLASS = 'search-highlight-current';

export const findRowElement = (textNode: Text, sectionElement: Element): Element | null => {
  let element: HTMLElement | null = textNode.parentElement;
  let depth = 0;

  while (element && element !== sectionElement && depth < MAX_PARENT_DEPTH) {
    if (!element.parentElement) break;

    const parentTag = element.parentElement.tagName;
    const parentComponent = (element.parentElement as HTMLElement).getAttribute('component');

    // Check if parent is a PRE tag or a Box with component="pre"
    if (parentTag === 'PRE' || parentComponent === 'pre') {
      return element;
    }

    element = element.parentElement as HTMLElement | null;
    depth++;
  }

  return null;
};

export const clearRowHighlights = (sectionElement: Element): void => {
  const allPreviousHighlights = sectionElement.querySelectorAll(`[${ROW_HIGHLIGHT_ATTR}="true"]`);
  allPreviousHighlights.forEach((row) => {
    row.removeAttribute(ROW_HIGHLIGHT_ATTR);
    row.classList.remove(ROW_HIGHLIGHT_CLASS);
    (row as HTMLElement).style.backgroundColor = '';
  });
};

export const applyRowHighlight = (row: Element): void => {
  row.setAttribute(ROW_HIGHLIGHT_ATTR, 'true');
  row.classList.add(ROW_HIGHLIGHT_CLASS);
  (row as HTMLElement).style.setProperty('background-color', colors.lemon[100], 'important');

  // Ensure child elements don't interfere with highlighting
  const children = row.querySelectorAll('*');
  children.forEach((child) => {
    if ((child as HTMLElement).style.backgroundColor) {
      (child as HTMLElement).style.backgroundColor = '';
    }
  });
};

export const highlightAllMatchRows = (
  sectionElement: Element,
  sectionMatches: SearchResult[],
  searchTerm: string,
): void => {
  clearRowHighlights(sectionElement);

  if (sectionMatches.length === 0) return;

  const targetOccurrenceIndices = new Set(sectionMatches.map((match) => match.occurrenceIndex));
  const rowsWithMatches = new Set<Element>();
  const walker = document.createTreeWalker(sectionElement, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      return shouldIncludeTextNode(node as Text)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  let occurrenceCount = 0;
  let currentNode: Node | null;

  while ((currentNode = walker.nextNode())) {
    const textNode = currentNode as Text;
    const text = textNode.textContent || '';
    const lowerText = text.toLowerCase();
    const lowerSearchTerm = searchTerm.toLowerCase();

    let searchIndex = 0;
    while ((searchIndex = lowerText.indexOf(lowerSearchTerm, searchIndex)) !== -1) {
      // Check if this occurrence is one of matches
      if (targetOccurrenceIndices.has(occurrenceCount)) {
        const row = findRowElement(textNode, sectionElement);
        if (row) {
          rowsWithMatches.add(row);
        }
      }
      occurrenceCount++;
      searchIndex += lowerSearchTerm.length;
    }
  }

  rowsWithMatches.forEach((row) => applyRowHighlight(row));
};

export const collectTextNodesToHighlight = (
  sectionElement: Element,
  searchTerm: string,
): Array<{ node: Text; parent: Node; text: string }> => {
  const textNodesToProcess: Array<{ node: Text; parent: Node; text: string }> = [];
  const regex = new RegExp(`(${escapeRegexp(searchTerm)})`, 'gi');
  const walker = document.createTreeWalker(sectionElement, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      return shouldIncludeTextNode(node as Text)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  let currentNode: Node | null;
  while ((currentNode = walker.nextNode())) {
    const textNode = currentNode as Text;
    const text = textNode.textContent || '';

    regex.lastIndex = 0;
    if (regex.test(text)) {
      textNodesToProcess.push({
        node: textNode,
        parent: textNode.parentNode!,
        text,
      });
    }
  }

  return textNodesToProcess;
};

export const applyMarksToTextNodes = (
  textNodesToProcess: Array<{ node: Text; parent: Node; text: string }>,
  searchTerm: string,
  sectionMatches: SearchResult[],
  currentMatchIndex: number | undefined,
  searchInDocumentationCard?: boolean,
): void => {
  const regex = new RegExp(`(${escapeRegexp(searchTerm)})`, 'gi');
  let localOccurrenceIndex = 0;

  textNodesToProcess.forEach(({ node, parent, text }) => {
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    regex.lastIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const correspondingResult = sectionMatches.find(
        (result) => result.occurrenceIndex === localOccurrenceIndex,
      );

      const mark = document.createElement('mark');
      mark.textContent = match[0];

      const isCurrent = correspondingResult && correspondingResult.matchIndex === currentMatchIndex;
      const style =
        isCurrent || !searchInDocumentationCard ? currentMatchMarkStyle : regularMatchMarkStyle;
      mark.setAttribute('style', styleObjectToString(style));
      mark.className = isCurrent ? CURRENT_MATCH_CLASS : 'search-highlight';

      fragment.appendChild(mark);
      lastIndex = match.index + match[0].length;
      localOccurrenceIndex++;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    parent.replaceChild(fragment, node);
  });
};

/**
 * Add highlight marks to text nodes in a section
 */
export const addHighlights = (
  section: Omit<PossibleSectionInDependencies, 'dependencies' | 'usedBy' | 'configurations'>,
  searchTerm: string,
  sectionMatches: SearchResult[],
  currentMatchIndex?: number,
  highlightEntireRow = false,
  searchInDocumentationCard?: boolean,
) => {
  const sectionElement = document.querySelector(`[data-section="${section}"]`);
  if (!sectionElement) return;

  removeHighlights(section);

  // Filter out data-based matches (those with tableType) - they're handled separately
  // Only apply DOM highlights to DOM-based matches
  const domOnlyMatches = sectionMatches.filter((match) => !match.tableType);

  // If highlighting entire rows, choose between highlighting only current or all matches
  if (highlightEntireRow) {
    highlightAllMatchRows(sectionElement, domOnlyMatches, searchTerm);
  }

  // Add marks to all matched text
  const textNodesToProcess = collectTextNodesToHighlight(sectionElement, searchTerm);
  applyMarksToTextNodes(
    textNodesToProcess,
    searchTerm,
    domOnlyMatches,
    currentMatchIndex,
    searchInDocumentationCard,
  );
};

/**
 * Remove all highlight marks from a section
 */
export const removeHighlights = (
  section: Omit<PossibleSectionInDependencies, 'dependencies' | 'usedBy' | 'configurations'>,
) => {
  const sectionElement = document.querySelector(`[data-section="${section}"]`);
  if (!sectionElement) return;

  cleanupHighlights(sectionElement);
};
